import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "../RestAPI";
import {
  FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiX,
  FiCheckCircle, FiXCircle, FiClock, FiCopy, FiExternalLink,
  FiChevronUp, FiChevronDown, FiZap
} from "react-icons/fi";
import AIFixPanel from "./AIFixPanel";
import "./ErrorList.css";

const STAGE_OPTIONS = [
  { value: "not_yet_taken", label: "Not Yet Taken", color: "#9e9e9e" },
  { value: "in_dev", label: "In Dev", color: "#2196f3" },
  { value: "in_qa", label: "In QA", color: "#ff9800" },
  { value: "qa_fail", label: "QA Fail", color: "#f44336" },
  { value: "in_uat", label: "In UAT", color: "#9c27b0" },
  { value: "production", label: "Production", color: "#4caf50" },
];

const getStageInfo = (stage) => STAGE_OPTIONS.find(s => s.value === stage) || STAGE_OPTIONS[0];

/* Compact label + tone for the AI Fix column in the table. */
const AI_FIX_BADGE = {
  queued: ["Queued", "info"],
  analyzing: ["Reading", "info"],
  patching: ["Fixing", "info"],
  verifying: ["Checking", "info"],
  branch_ready: ["Branch", "success"],
  needs_review: ["Needs review", "warn"],
  no_code_fix: ["Not code", "warn"],
  rejected: ["Rejected", "danger"],
  failed: ["Failed", "danger"],
  cancelled: ["Cancelled", "muted"],
};

const ErrorList = () => {
  const today = new Date().toISOString().split("T")[0];
  const [errors, setErrors] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total_count: 0, total_pages: 0 });
  const [totalErrors, setTotalErrors] = useState(0);
  const [filters, setFilters] = useState({
    module: "", severity: "", category: "", organization: "",
    status_code: "", stage: "", is_analyzed: "", recurring: "",
    screen_path: "", search: "", date_from: today, date_to: today,
    method: "",
  });
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [stageHistory, setStageHistory] = useState([]);
  const [listSummary, setListSummary] = useState(null);
  const [sortBy, setSortBy] = useState("latest_timestamp");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkStage, setBulkStage] = useState("");
  const [bulkFixing, setBulkFixing] = useState(false);

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  };

  const fetchErrors = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("page_size", pagination.page_size);
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder);
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });

      const res = await fetch(`${API_BASE_URL}error-tracking/errors/?${params}`, { headers });
      const data = await res.json();
      if (data.status === "success") {
        setErrors(data.data || []);
        setPagination(data.pagination || {});
        setTotalErrors(data.total_errors || 0);
        setSelectedRows([]);
      }
    } catch (err) {
      console.error("Error fetching errors:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page_size, sortBy, sortOrder]);

  const fetchListSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      const res = await fetch(`${API_BASE_URL}error-tracking/errors/summary/?${params}`, { headers });
      const data = await res.json();
      if (data.status === "success") setListSummary(data.data);
    } catch (err) {
      console.error("Error fetching summary:", err);
    }
  }, [filters]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/filters/`, { headers });
      const data = await res.json();
      if (data.status === "success") setFilterOptions(data.data);
    } catch (err) {
      console.error("Error fetching filters:", err);
    }
  }, []);

  const fetchStageHistory = async (fingerprint, batchNumber) => {
    try {
      const params = new URLSearchParams({ error_fingerprint: fingerprint });
      if (batchNumber) params.append("batch_number", batchNumber);
      const res = await fetch(`${API_BASE_URL}error-tracking/errors/stage-history/?${params}`, { headers });
      const data = await res.json();
      if (data.status === "success") {
        setStageHistory(data.data || []);
      }
    } catch (err) {
      console.error("Error fetching stage history:", err);
    }
  };

  const fetchErrorDetail = async (id, fingerprint, batchNumber) => {
    setDetailLoading(true);
    setStageHistory([]);
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/errors/detail/?id=${id}`, { headers });
      const data = await res.json();
      if (data.status === "success") {
        setSelectedError({ ...data.data, related_occurrences: data.related_occurrences, related_count: data.related_count });
      }
      if (fingerprint) {
        fetchStageHistory(fingerprint, batchNumber);
      }
    } catch (err) {
      console.error("Error fetching detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const updateStage = async (fingerprint, batchNumber, newStage) => {
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/errors/update-stage/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ error_fingerprint: fingerprint, batch_number: batchNumber, stage: newStage }),
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchErrors(pagination.page);
        if (selectedError) {
          setSelectedError(prev => ({ ...prev, stage: newStage, is_resolved: newStage === "production" }));
        }
      }
    } catch (err) {
      console.error("Error updating stage:", err);
    }
  };

  const toggleRowSelection = (fingerprint, batchNumber) => {
    const key = `${fingerprint}-${batchNumber}`;
    setSelectedRows(prev =>
      prev.some(r => r.key === key)
        ? prev.filter(r => r.key !== key)
        : [...prev, { key, error_fingerprint: fingerprint, batch_number: batchNumber }]
    );
  };

  const toggleSelectAll = () => {
    if (selectedRows.length === errors.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(errors.map(e => ({
        key: `${e.error_fingerprint}-${e.batch_number}`,
        error_fingerprint: e.error_fingerprint,
        batch_number: e.batch_number,
      })));
    }
  };

  const bulkUpdateStage = async () => {
    if (!bulkStage || selectedRows.length === 0) return;
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/errors/bulk-update-stage/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          items: selectedRows.map(r => ({ error_fingerprint: r.error_fingerprint, batch_number: r.batch_number })),
          stage: bulkStage,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setSelectedRows([]);
        setBulkStage("");
        fetchErrors(pagination.page);
        fetchListSummary();
      }
    } catch (err) {
      console.error("Bulk update error:", err);
    }
  };

  const bulkQueueAIFix = async () => {
    if (selectedRows.length === 0) return;
    setBulkFixing(true);
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/ai-fix/bulk-enqueue/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          patterns: selectedRows.map(r => ({
            error_fingerprint: r.error_fingerprint,
            batch_number: r.batch_number,
          })),
          mode: "patch",
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setSelectedRows([]);
        fetchErrors(pagination.page);
      }
      alert(data.message || "Done");
    } catch (err) {
      console.error("Bulk AI fix error:", err);
      alert("Could not reach the server");
    } finally {
      setBulkFixing(false);
    }
  };

  useEffect(() => {
    fetchErrors();
    fetchListSummary();
    fetchFilterOptions();
  }, []);

  const handleSearch = () => { fetchErrors(1); fetchListSummary(); };
  const handlePageChange = (page) => fetchErrors(page);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  useEffect(() => {
    fetchErrors(1);
  }, [sortBy, sortOrder]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      module: "", severity: "", category: "", organization: "",
      status_code: "", stage: "", is_analyzed: "", recurring: "",
      screen_path: "", search: "", date_from: today, date_to: today,
      method: "",
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "-";
    return new Date(ts).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  };

  return (
    <div className="el-page">
      {/* Header */}
      <div className="el-header">
        <h2 className="el-header__title">Error List</h2>
        <div className="el-header__actions">
          <div className="el-search-box">
            <FiSearch size={14} />
            <input
              type="text"
              placeholder="Search URL, root cause, org..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          <button className={`el-btn el-btn--outline ${showFilters ? "el-btn--active" : ""}`} onClick={() => setShowFilters(!showFilters)}>
            <FiFilter size={13} /> Filters
          </button>
          <button className="el-btn el-btn--primary" onClick={handleSearch}>Search</button>
        </div>
      </div>

      {/* Summary Cards */}
      {listSummary && (
        <div className="el-summary-cards">
          <div className="el-summary-card">
            <span className="el-summary-card__value">{listSummary.total_errors}</span>
            <span className="el-summary-card__label">Total Errors</span>
          </div>
          <div className="el-summary-card el-summary-card--get">
            <span className="el-summary-card__value">{listSummary.method_counts?.GET || 0}</span>
            <span className="el-summary-card__label">GET</span>
          </div>
          <div className="el-summary-card el-summary-card--post">
            <span className="el-summary-card__value">{listSummary.method_counts?.POST || 0}</span>
            <span className="el-summary-card__label">POST</span>
          </div>
          <div className="el-summary-card el-summary-card--put">
            <span className="el-summary-card__value">{listSummary.method_counts?.PUT || 0}</span>
            <span className="el-summary-card__label">PUT</span>
          </div>
          <div className="el-summary-card el-summary-card--patch">
            <span className="el-summary-card__value">{listSummary.method_counts?.PATCH || 0}</span>
            <span className="el-summary-card__label">PATCH</span>
          </div>
          <div className="el-summary-card el-summary-card--delete">
            <span className="el-summary-card__value">{listSummary.method_counts?.DELETE || 0}</span>
            <span className="el-summary-card__label">DELETE</span>
          </div>
          <div className="el-summary-card">
            <span className="el-summary-card__value">{listSummary.total_patterns}</span>
            <span className="el-summary-card__label">Patterns</span>
          </div>
        </div>
      )}

      {/* Stage Counts */}
      {listSummary?.stage_counts && (
        <div className="el-stage-pipeline">
          {STAGE_OPTIONS.map(s => (
            <div key={s.value} className="el-stage-pill" style={{ borderColor: s.color }}>
              <span className="el-stage-pill__dot" style={{ background: s.color }} />
              <span className="el-stage-pill__label">{s.label}</span>
              <span className="el-stage-pill__count">{listSummary.stage_counts[s.value] || 0}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filter Panel */}
      {showFilters && (
        <div className="el-filters">
          <div className="el-filters__grid">
            <div className="el-filter-group">
              <label>Date From</label>
              <input type="date" value={filters.date_from} onChange={(e) => handleFilterChange("date_from", e.target.value)} />
            </div>
            <div className="el-filter-group">
              <label>Date To</label>
              <input type="date" value={filters.date_to} onChange={(e) => handleFilterChange("date_to", e.target.value)} />
            </div>
            <div className="el-filter-group">
              <label>Module</label>
              <select value={filters.module} onChange={(e) => handleFilterChange("module", e.target.value)}>
                <option value="">All Modules</option>
                {filterOptions?.modules?.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Severity</label>
              <select value={filters.severity} onChange={(e) => handleFilterChange("severity", e.target.value)}>
                <option value="">All</option>
                {filterOptions?.severities?.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Category</label>
              <select value={filters.category} onChange={(e) => handleFilterChange("category", e.target.value)}>
                <option value="">All</option>
                {filterOptions?.categories?.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Organization</label>
              <select value={filters.organization} onChange={(e) => handleFilterChange("organization", e.target.value)}>
                <option value="">All</option>
                {filterOptions?.organizations?.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Status Code</label>
              <select value={filters.status_code} onChange={(e) => handleFilterChange("status_code", e.target.value)}>
                <option value="">All</option>
                {filterOptions?.status_codes?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Method</label>
              <select value={filters.method} onChange={(e) => handleFilterChange("method", e.target.value)}>
                <option value="">All Methods</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div className="el-filter-group">
              <label>Stage</label>
              <select value={filters.stage} onChange={(e) => handleFilterChange("stage", e.target.value)}>
                <option value="">All Stages</option>
                {(filterOptions?.stages || STAGE_OPTIONS).map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Analyzed</label>
              <select value={filters.is_analyzed} onChange={(e) => handleFilterChange("is_analyzed", e.target.value)}>
                <option value="">All</option>
                <option value="true">Analyzed</option>
                <option value="false">Not Analyzed</option>
              </select>
            </div>
            <div className="el-filter-group el-filter-group--checkbox">
              <label className="el-checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.recurring === "true"}
                  onChange={(e) => handleFilterChange("recurring", e.target.checked ? "true" : "")}
                />
                <span>Recurring Only (Batch &gt; 1)</span>
              </label>
            </div>
          </div>
          <div className="el-filters__actions">
            <button className="el-btn el-btn--ghost" onClick={clearFilters}>Clear All</button>
            <button className="el-btn el-btn--primary" onClick={handleSearch}>Apply</button>
          </div>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedRows.length > 0 && (
        <div className="el-bulk-bar">
          <span className="el-bulk-bar__count">{selectedRows.length} selected</span>
          <select className="el-stage-select" value={bulkStage} onChange={(e) => setBulkStage(e.target.value)}>
            <option value="">Move to...</option>
            {STAGE_OPTIONS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button className="el-btn el-btn--primary" onClick={bulkUpdateStage} disabled={!bulkStage}>
            Update Stage
          </button>
          <button className="el-btn el-btn--outline" onClick={bulkQueueAIFix} disabled={bulkFixing}>
            <FiZap size={13} /> {bulkFixing ? "Queueing..." : `Fix with AI (${selectedRows.length})`}
          </button>
          <button className="el-btn el-btn--ghost" onClick={() => setSelectedRows([])}>
            Cancel
          </button>
        </div>
      )}

      {/* Error Table */}
      <div className="el-table-wrap">
        {loading ? (
          <div className="el-loading">
            <div className="el-loading__spinner" />
            <span>Loading errors...</span>
          </div>
        ) : errors.length === 0 ? (
          <div className="el-empty">No errors found</div>
        ) : (
          <table className="el-table">
            <thead>
              <tr>
                <th className="el-th--checkbox">
                  <input
                    type="checkbox"
                    checked={errors.length > 0 && selectedRows.length === errors.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="el-th--sortable" onClick={() => handleSort("latest_timestamp")}>
                  Latest Time {sortBy === "latest_timestamp" && (sortOrder === "desc" ? <FiChevronDown size={12} /> : <FiChevronUp size={12} />)}
                </th>
                <th>Method</th>
                <th>URL</th>
                <th>Status</th>
                <th>Module</th>
                <th>Severity</th>
                <th>Category</th>
                <th className="el-th--sortable" onClick={() => handleSort("total_occurrences")}>
                  Occurrences {sortBy === "total_occurrences" && (sortOrder === "desc" ? <FiChevronDown size={12} /> : <FiChevronUp size={12} />)}
                </th>
                <th>User</th>
                <th>Affected Orgs</th>
                <th>Stage</th>
                <th>AI Fix</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((err) => {
                const stageInfo = getStageInfo(err.stage);
                return (
                <tr key={`${err.error_fingerprint}-${err.batch_number}`} className={`el-row ${err.severity === "critical" ? "el-row--critical" : ""} ${selectedRows.some(r => r.key === `${err.error_fingerprint}-${err.batch_number}`) ? "el-row--selected" : ""}`}>
                  <td className="el-cell--checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRows.some(r => r.key === `${err.error_fingerprint}-${err.batch_number}`)}
                      onChange={() => toggleRowSelection(err.error_fingerprint, err.batch_number)}
                    />
                  </td>
                  <td className="el-cell--time">{formatTimestamp(err.latest_timestamp)}</td>
                  <td><span className={`et-method et-method--${err.method?.toLowerCase()}`}>{err.method}</span></td>
                  <td className="el-cell--url" title={err.url}>{err.url?.length > 50 ? err.url.slice(0, 50) + "..." : err.url}</td>
                  <td><span className="et-status-badge">{err.status_code}</span></td>
                  <td>{err.module || "-"}</td>
                  <td><span className={`et-severity et-severity--${err.severity}`}>{err.severity || "-"}</span></td>
                  <td className="el-cell--cat">{err.category || "-"}</td>
                  <td className="el-cell--count">
                    <span className="el-occurrence-badge">{err.total_occurrences}</span>
                    {err.total_batches > 1 && <span className="el-batch-badge">Batch #{err.batch_number}</span>}
                  </td>
                  <td className="el-cell--user" title={err.user_name}>{err.user_name || "-"}</td>
                  <td className="el-cell--org" title={err.affected_orgs?.join(", ")}>
                    {err.affected_orgs?.length > 0
                      ? err.affected_orgs.length <= 2
                        ? err.affected_orgs.join(", ")
                        : `${err.affected_orgs[0]} +${err.affected_orgs.length - 1}`
                      : "-"}
                  </td>
                  <td>
                    <div className="el-state-badges">
                      <span className="el-stage-badge" style={{ background: stageInfo.color }}>{stageInfo.label}</span>
                      {err.is_analyzed ? <span className="el-badge el-badge--analyzed">AI</span> : null}
                    </div>
                  </td>
                  <td>
                    {(() => {
                      const fix = err.latest_ai_fix;
                      if (!fix) return <span className="el-aifix-dash">—</span>;
                      const [label, tone] = AI_FIX_BADGE[fix.status] || [fix.status, "muted"];
                      return (
                        <span
                          className={`el-aifix-badge el-aifix-badge--${tone}`}
                          title={fix.branch_name ? `git checkout ${fix.branch_name}` : fix.status}
                        >
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    <div className="el-actions">
                      <button className="el-action-btn" title="View Detail" onClick={() => fetchErrorDetail(err.id, err.error_fingerprint, err.batch_number)}>
                        <FiExternalLink size={13} />
                      </button>
                      <select
                        className="el-stage-select"
                        value={err.stage || "not_yet_taken"}
                        onChange={(e) => updateStage(err.error_fingerprint, err.batch_number, e.target.value)}
                      >
                        {STAGE_OPTIONS.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="el-pagination">
          <span className="el-pagination__info">
            Page {pagination.page} of {pagination.total_pages} &middot; {pagination.total_count} patterns ({totalErrors} total errors)
          </span>
          <div className="el-pagination__btns">
            <button
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
              className="el-page-btn"
            ><FiChevronLeft size={14} /></button>
            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
              let pageNum;
              if (pagination.total_pages <= 5) {
                pageNum = i + 1;
              } else if (pagination.page <= 3) {
                pageNum = i + 1;
              } else if (pagination.page >= pagination.total_pages - 2) {
                pageNum = pagination.total_pages - 4 + i;
              } else {
                pageNum = pagination.page - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`el-page-btn ${pagination.page === pageNum ? "el-page-btn--active" : ""}`}
                  onClick={() => handlePageChange(pageNum)}
                >{pageNum}</button>
              );
            })}
            <button
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => handlePageChange(pagination.page + 1)}
              className="el-page-btn"
            ><FiChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedError && (
        <div className="el-modal-overlay" onClick={() => setSelectedError(null)}>
          <div className="el-modal" onClick={(e) => e.stopPropagation()}>
            <div className="el-modal__header">
              <h3>Error Detail #{selectedError.id}</h3>
              <div className="el-modal__header-actions">
                <select
                  className="el-stage-select el-stage-select--modal"
                  value={selectedError.stage || "not_yet_taken"}
                  onChange={(e) => updateStage(selectedError.error_fingerprint, selectedError.batch_number || 1, e.target.value)}
                >
                  {STAGE_OPTIONS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <button className="el-modal__close" onClick={() => setSelectedError(null)}><FiX size={18} /></button>
              </div>
            </div>

            {detailLoading ? (
              <div className="el-loading"><div className="el-loading__spinner" /></div>
            ) : (
              <div className="el-modal__body">
                {/* Basic Info */}
                <div className="el-detail-section">
                  <h4>Request Info</h4>
                  <div className="el-detail-grid">
                    <div className="el-detail-item">
                      <span className="el-detail-label">Method</span>
                      <span className={`et-method et-method--${selectedError.method?.toLowerCase()}`}>{selectedError.method}</span>
                    </div>
                    <div className="el-detail-item">
                      <span className="el-detail-label">Status Code</span>
                      <span className="et-status-badge">{selectedError.status_code}</span>
                    </div>
                    <div className="el-detail-item">
                      <span className="el-detail-label">Severity</span>
                      <span className={`et-severity et-severity--${selectedError.severity}`}>{selectedError.severity || "unknown"}</span>
                    </div>
                    <div className="el-detail-item">
                      <span className="el-detail-label">Category</span>
                      <span>{selectedError.category || "-"}</span>
                    </div>
                    <div className="el-detail-item">
                      <span className="el-detail-label">Timestamp</span>
                      <span>{formatTimestamp(selectedError.timestamp)}</span>
                    </div>
                    <div className="el-detail-item">
                      <span className="el-detail-label">Module</span>
                      <span>{selectedError.module || "-"}</span>
                    </div>
                    <div className="el-detail-item el-detail-item--full">
                      <span className="el-detail-label">URL</span>
                      <span className="el-detail-url">
                        {selectedError.url}
                        <button className="el-copy-btn" onClick={() => copyToClipboard(selectedError.url)}><FiCopy size={11} /></button>
                      </span>
                    </div>
                    {selectedError.query_params && (
                      <div className="el-detail-item el-detail-item--full">
                        <span className="el-detail-label">Query Parameters</span>
                        <pre className="el-detail-code el-detail-code--compact">{selectedError.query_params}</pre>
                      </div>
                    )}
                  </div>
                </div>

                {/* Context */}
                <div className="el-detail-section">
                  <h4>Context</h4>
                  <div className="el-detail-grid">
                    <div className="el-detail-item"><span className="el-detail-label">Organization</span><span>{selectedError.organization_name || "-"}</span></div>
                    <div className="el-detail-item"><span className="el-detail-label">Tenant DB</span><span>{selectedError.tenant_db || "-"}</span></div>
                    <div className="el-detail-item"><span className="el-detail-label">User Email</span><span>{selectedError.user_email || "-"}</span></div>
                    <div className="el-detail-item"><span className="el-detail-label">Client IP</span><span>{selectedError.client_ip || "-"}</span></div>
                    <div className="el-detail-item"><span className="el-detail-label">Screen Path</span><span className="el-detail-url">{selectedError.screen_path || "-"}{selectedError.screen_path && <button className="el-copy-btn" onClick={() => copyToClipboard(selectedError.screen_path)}><FiCopy size={11} /></button>}</span></div>
                    <div className="el-detail-item"><span className="el-detail-label">Request ID</span><span>{selectedError.request_id || "-"}</span></div>
                  </div>
                </div>

                {/* AI Fix Agent — analyse the backend code and prepare a branch */}
                <AIFixPanel
                  error={selectedError}
                  onJobChange={() => fetchErrors(pagination.page)}
                />

                {/* AI Analysis */}
                {selectedError.ai_analysis && (
                  <div className="el-detail-section">
                    <h4>AI Analysis</h4>
                    <div className="el-detail-block">{selectedError.ai_analysis}</div>
                  </div>
                )}

                {selectedError.root_cause && (
                  <div className="el-detail-section">
                    <h4>Root Cause</h4>
                    <div className="el-detail-block el-detail-block--warning">{selectedError.root_cause}</div>
                  </div>
                )}

                {selectedError.fix_suggestion && (
                  <div className="el-detail-section">
                    <h4>Fix Suggestion</h4>
                    <div className="el-detail-block el-detail-block--success">{selectedError.fix_suggestion}</div>
                  </div>
                )}

                {/* Traceback */}
                {selectedError.traceback_info && (
                  <div className="el-detail-section">
                    <h4>Traceback</h4>
                    <pre className="el-detail-code">{selectedError.traceback_info}</pre>
                  </div>
                )}

                {/* Request/Response Bodies */}
                {selectedError.request_body && (
                  <div className="el-detail-section">
                    <h4>Request Body</h4>
                    <pre className="el-detail-code">{selectedError.request_body}</pre>
                  </div>
                )}

                {selectedError.response_body && (
                  <div className="el-detail-section">
                    <h4>Response Body</h4>
                    <pre className="el-detail-code">{selectedError.response_body}</pre>
                  </div>
                )}

                {/* Stage Change History */}
                {stageHistory.length > 0 && (
                  <div className="el-detail-section">
                    <h4>Stage History</h4>
                    <div className="el-history-timeline">
                      {stageHistory.map((h, i) => {
                        const toInfo = getStageInfo(h.to_stage);
                        return (
                          <div key={i} className="el-history-item">
                            <div className="el-history-item__dot" style={{ background: toInfo.color }} />
                            <div className="el-history-item__content">
                              <div className="el-history-item__transition">
                                {h.from_stage ? (
                                  <>
                                    <span className="el-history-stage">{h.from_stage_label}</span>
                                    <span className="el-history-arrow">&rarr;</span>
                                    <span className="el-history-stage" style={{ color: toInfo.color, fontWeight: 600 }}>{h.to_stage_label}</span>
                                  </>
                                ) : (
                                  <span className="el-history-stage" style={{ color: toInfo.color, fontWeight: 600 }}>Created as {h.to_stage_label}</span>
                                )}
                              </div>
                              <div className="el-history-item__meta">
                                <span className="el-history-who">{h.changed_by_name || "System"}</span>
                                <span className="el-history-when">{formatTimestamp(h.changed_at)}</span>
                              </div>
                              {h.notes && <div className="el-history-item__notes">{h.notes}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Related Occurrences */}
                {selectedError.related_occurrences?.length > 0 && (
                  <div className="el-detail-section">
                    <h4>Related Occurrences ({selectedError.related_count})</h4>
                    <div className="el-table-wrap">
                      <table className="el-table el-table--compact">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Organization</th>
                            <th>Screen</th>
                            <th>IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedError.related_occurrences.map((r, i) => (
                            <tr key={i}>
                              <td>{formatTimestamp(r.timestamp)}</td>
                              <td>{r.organization_name || "-"}</td>
                              <td>{r.screen_path || "-"}</td>
                              <td>{r.client_ip || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorList;
